<script setup lang="ts">
import { ref } from "vue";
import type { ComboboxInputProps } from "./combobox.types";
import { useComboboxInput } from "./useCombobox.Input";
import InputTag from "components/Input/InputTag.vue";

defineOptions({ name: "ComboboxInput" });

const props = defineProps<ComboboxInputProps>();

const inputRef = ref<HTMLInputElement | null>(null);

const {
  isLeading,
  leadingIconName,
  isMultiple,
  tags,
  getTagDisplayValue,
  removeTag,
  placeholder,
  inputValue,
  classes,
  tagBindings,
  reorderContainerBindings,
  combobox,
  handleInput,
  handleFocus,
  handleBlur,
  handleKeydown,
} = useComboboxInput({ props, inputRef });
</script>

<template>
  <div
    part="base"
    exportparts="leading-icon"
    :class="classes.base"
    class="e-combobox-input"
    role="combobox"
    :aria-expanded="combobox.isOpen.value"
    aria-haspopup="listbox"
    :aria-disabled="combobox.comboboxProps.value?.disabled || undefined"
    :data-multiple="isMultiple || undefined"
    v-bind="reorderContainerBindings()"
    @click="inputRef?.focus()"
  >
    <span
      v-show="isLeading"
      part="leading"
      :class="classes.icon"
      class="e-combobox-input-icon e-combobox-input-leading"
    >
      <slot name="leading">
        <e-icon v-if="leadingIconName" exportparts="base:leading-icon" :name="leadingIconName" size="sm" />
      </slot>
    </span>

    <input-tag
      v-for="(tag, index) in tags"
      :key="String(tag)"
      v-bind="tagBindings(index)"
      class="e-combobox-tag"
      :value="tag"
      :display-value="getTagDisplayValue(tag)"
      :color="combobox.comboboxProps.value?.color"
      :size="combobox.comboboxProps.value?.size"
      :disabled="combobox.comboboxProps.value?.disabled"
      @remove="removeTag(tag)"
    />

    <input
      ref="inputRef"
      part="input"
      :class="classes.input"
      class="e-combobox-input-field"
      type="text"
      :value="inputValue"
      :placeholder="tags.length === 0 ? placeholder : ''"
      :disabled="combobox.comboboxProps.value?.disabled"
      autocomplete="off"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown="handleKeydown"
    />

    <span part="trigger" :class="classes.trigger" class="e-combobox-input-trigger">
      <slot name="trigger">
        <slot />
      </slot>
    </span>
  </div>
</template>

<style>
:host {
  display: contents;
}
</style>
